// src/encounter/lib/note-delta.ts
//
// Wire ⇄ Quill-Delta helpers for the clinical section note.
//
// The engine is `react-quill-new` (Quill 2.0.3) — the SAME control the app already uses for
// macros (src/settings/components/macro/macro-edit-dialog.tsx), so Quill itself does the
// serialising: `editor.getContents()` is the Delta, `editor.getText()` the plain text. This
// module only holds the two edges Quill doesn't cover:
//
//   · READING a stored value that may not be a Delta. Flutter writes
//     `jsonEncode(document.toDelta().toJson())` — a bare ops ARRAY (quil_text_editor.dart
//     `encodeDocument`) — but a note captured before the rich editor existed is plain text, and
//     a corrupt row must not brick the editor. Mirrors the macro dialog's parse-or-empty rule.
//   · WRITING the pair the complaint document wants: `comment` (the stringified ops array,
//     which is exactly what Flutter's `tryParseDocument` reads back) + `notePlainText`.
//
// Related: src/dashboard/quill-delta.ts holds the one-liner the phone-call surface needs
// (plain text → a bare ops array) because that surface has no editor. Same wire convention,
// different need — don't add a third.
//
// Deliberately NOT here: any HTML conversion. Rendering a stored note is a read-only
// `ReactQuill`, the same way the macro history dialog renders a snapshot — one engine for both
// directions, so a note can never render differently from how it was authored.

/** Quill's empty document (the macro dialog's `EMPTY_DELTA_OPS`). */
export const EMPTY_DELTA_OPS: DeltaOp[] = [{ insert: '\n' }];

export interface DeltaOp {
  insert?: string | Record<string, unknown>;
  attributes?: Record<string, unknown>;
}

/**
 * A stored note → Delta ops for `ReactQuill`.
 * - a Flutter/Quill ops array (or a `{ops:[…]}` wrapper) parses through;
 * - legacy plain text becomes a single insert, so old notes still open;
 * - anything unparseable degrades to the empty document rather than throwing.
 */
export function parseNoteOps(stored: string | null | undefined): DeltaOp[] {
  const raw = (stored ?? '').trim();
  if (!raw) return EMPTY_DELTA_OPS;
  if (raw.startsWith('[') || raw.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as DeltaOp[];
      const ops = (parsed as { ops?: unknown }).ops;
      if (Array.isArray(ops)) return ops as DeltaOp[];
    } catch {
      // not JSON after all — treat it as the plain text it appears to be
    }
    return EMPTY_DELTA_OPS;
  }
  return [{ insert: raw.endsWith('\n') ? raw : `${raw}\n` }];
}

/** Does this note hold anything? (Quill's empty document is a lone newline.) */
export function isEmptyNote(stored: string | null | undefined): boolean {
  return deltaPlainText(parseNoteOps(stored)).trim() === '';
}

/** The ops' plain text — the fallback when no live Quill editor is at hand. */
export function deltaPlainText(ops: DeltaOp[]): string {
  return ops.map((op) => (typeof op.insert === 'string' ? op.insert : '')).join('');
}

/** The wire pair. An empty document saves as '' / '' so a cleared note really clears. */
export function serializeNote(ops: DeltaOp[], plainText: string): { comment: string; plainText: string } {
  const text = plainText.trim();
  if (!text) return { comment: '', plainText: '' };
  return { comment: JSON.stringify(ops), plainText: text };
}
